import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Chip,
  CircularProgress,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Alert,
  Divider,
  Autocomplete,
} from '@mui/material';
import { Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { getCameraSocketUrl } from '../../utils/cameraSocketUrl';
import { getCameraIoOptions } from '../../utils/cameraIoOptions';

const AdminCameraDashboard = () => {
  const socketRef = useRef(null);
  const urlCacheRef = useRef({});
  const [streamers, setStreamers] = useState([]);
  const [imgUrls, setImgUrls] = useState({});
  const [summaryDate, setSummaryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [summaries, setSummaries] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [streamersApiError, setStreamersApiError] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [policyMode, setPolicyMode] = useState('restricted');
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [recordStreams, setRecordStreams] = useState(false);
  const [contactTypeOptions, setContactTypeOptions] = useState([]);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [userPickOptions, setUserPickOptions] = useState([]);
  const [userPickLoading, setUserPickLoading] = useState(false);
  const [userSearchInput, setUserSearchInput] = useState('');
  const userSearchDebounceRef = useRef(null);

  const applyFrame = useCallback((userId, buffer) => {
    if (!buffer) return;
    const blob = new Blob([buffer], { type: 'image/jpeg' });
    const old = urlCacheRef.current[userId];
    if (old) URL.revokeObjectURL(old);
    const u = URL.createObjectURL(blob);
    urlCacheRef.current[userId] = u;
    setImgUrls((prev) => ({ ...prev, [userId]: u }));
  }, []);

  const fetchStreamers = useCallback(async () => {
    try {
      const r = await api.get('/camera/admin/streamers', { skipErrorToast: true });
      const list = r.data?.data?.streamers || [];
      setStreamers(list);
      setStreamersApiError(null);
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        (e.code === 'ERR_NETWORK'
          ? 'Network error — is the API running and CORS enabled? Check that the UI uses the same API base as in api.js (port-forward / REACT_APP_API_URL).'
          : 'Could not load active streamers.');
      setStreamersApiError(msg);
      setStreamers([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const r = await api.get('/camera/admin/summary', { params: { date: summaryDate } });
      setSummaries(r.data?.data?.summaries || []);
    } catch {
      toast.error('Could not load summary');
    } finally {
      setLoadingSummary(false);
    }
  }, [summaryDate]);

  const loadPolicy = useCallback(async () => {
    setLoadingPolicy(true);
    try {
      const r = await api.get('/camera/admin/policy');
      const d = r.data?.data;
      const p = d?.policy;
      if (p) {
        setPolicyMode(p.mode === 'any' ? 'any' : 'restricted');
        setAllowedTypes(Array.isArray(p.allowed_contact_types) ? p.allowed_contact_types : []);
        setRecordStreams(Boolean(p.admin_recording_enabled));
      }
      setAllowedUsers(Array.isArray(d?.allowed_users) ? d.allowed_users : []);
      if (Array.isArray(d?.contact_types)) {
        setContactTypeOptions(d.contact_types);
      }
    } catch {
      toast.error('Could not load camera policy');
    } finally {
      setLoadingPolicy(false);
    }
  }, []);

  const runUserPicklistSearch = useCallback(async (q) => {
    const s = String(q || '').trim();
    if (s.length < 1) {
      setUserPickOptions([]);
      return;
    }
    setUserPickLoading(true);
    try {
      const r = await api.get('/camera/admin/user-picklist', {
        params: { search: s },
        skipErrorToast: true,
      });
      setUserPickOptions(r.data?.data?.users || []);
    } catch {
      setUserPickOptions([]);
    } finally {
      setUserPickLoading(false);
    }
  }, []);

  const scheduleUserPicklistSearch = (value) => {
    if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
    userSearchDebounceRef.current = setTimeout(() => runUserPicklistSearch(value), 300);
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const payload = {
        mode: policyMode,
        admin_recording_enabled: recordStreams,
      };
      if (policyMode === 'restricted') {
        payload.allowed_contact_types = allowedTypes;
        payload.allowed_user_ids = allowedUsers.map((u) => u.id);
      }
      await api.put('/camera/admin/policy', payload);
      toast.success('Camera policy saved');
      await loadPolicy();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const toggleAllowedType = (code) => {
    setAllowedTypes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  useEffect(() => {
    fetchStreamers();
    const id = setInterval(fetchStreamers, 8000);
    return () => clearInterval(id);
  }, [fetchStreamers]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  useEffect(() => {
    const token = getAuthAccessToken();
    if (!token) return undefined;
    const socketUrl = getCameraSocketUrl();
    const s = io(socketUrl, getCameraIoOptions(token));
    socketRef.current = s;
    let connectErrToastShown = false;
    s.on('connect', () => {
      connectErrToastShown = false;
    });
    s.on('connect_error', (err) => {
      console.error('[camera socket] connect_error', err?.message || err, { url: socketUrl, path: s.io?.opts?.path });
      if (!connectErrToastShown) {
        connectErrToastShown = true;
        toast.error(
          'Camera live server could not connect. Match REACT_APP_API_URL to your API at build time; check ingress routes /api to the backend. See browser console (F12) for details.'
        );
      }
    });
    s.on('camera:frame', (userId, buf) => {
      applyFrame(userId, buf);
    });
    return () => {
      s.disconnect();
      socketRef.current = null;
      Object.values(urlCacheRef.current).forEach((u) => URL.revokeObjectURL(u));
      urlCacheRef.current = {};
    };
  }, [applyFrame]);

  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;
    streamers.forEach((st) => {
      if (!st.stale) {
        s.emit('camera:watch', { targetUserId: st.userId });
      }
    });
  }, [streamers]);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Camera monitor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure who may use My camera, optional server-side recording, and watch live thumbnails from users
        who are streaming. Stale rows mean no frames for ~15s (paused or offline).
      </Typography>

      {streamersApiError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStreamersApiError(null)}>
          {streamersApiError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Who can use &quot;My camera&quot;
          </Typography>
          {loadingPolicy ? (
            <CircularProgress size={28} />
          ) : (
            <>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">Eligibility</FormLabel>
                <RadioGroup
                  value={policyMode}
                  onChange={(e) => setPolicyMode(e.target.value)}
                >
                  <FormControlLabel
                    value="restricted"
                    control={<Radio />}
                    label="Restricted — only users and/or contact types I add below can stream"
                  />
                  <FormControlLabel
                    value="any"
                    control={<Radio />}
                    label="Open — any user who has the camera module (and role permissions) can stream"
                  />
                </RadioGroup>
              </FormControl>

              {policyMode === 'restricted' && (
                <Box sx={{ mb: 2, pl: { sm: 0 } }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Allowed users
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Search by name or email and select a user to add them individually. This does not require
                    Users admin permission.
                  </Typography>
                  <Autocomplete
                    size="small"
                    sx={{ maxWidth: 560, mb: 1.5 }}
                    filterOptions={(opts) => opts}
                    options={userPickOptions.filter((o) => !allowedUsers.some((u) => u.id === o.id))}
                    loading={userPickLoading}
                    inputValue={userSearchInput}
                    onInputChange={(_, v) => {
                      setUserSearchInput(v);
                      scheduleUserPicklistSearch(v);
                    }}
                    onChange={(_, option) => {
                      if (option && !allowedUsers.some((u) => u.id === option.id)) {
                        setAllowedUsers((prev) => [...prev, option]);
                      }
                      setUserSearchInput('');
                      setUserPickOptions([]);
                    }}
                    getOptionLabel={(o) =>
                      o && o.first_name != null
                        ? `${o.first_name} ${o.last_name} (${o.email})`
                        : ''
                    }
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    value={null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Add user to allowlist"
                        placeholder="Type to search…"
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {allowedUsers.map((u) => (
                      <Chip
                        key={u.id}
                        label={`${u.first_name} ${u.last_name}`}
                        onDelete={() =>
                          setAllowedUsers((prev) => prev.filter((x) => x.id !== u.id))
                        }
                      />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Allowed contact types
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Anyone linked to a contact with this type (employee record or contact email matching their
                    login) can stream. OR logic with the user list above.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {contactTypeOptions.map((t) => (
                      <FormControlLabel
                        key={t}
                        control={
                          <Checkbox
                            checked={allowedTypes.includes(t)}
                            onChange={() => toggleAllowedType(t)}
                          />
                        }
                        label={t}
                      />
                    ))}
                  </Box>
                  {allowedUsers.length === 0 && allowedTypes.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No users or contact types selected — non-admin users cannot start streaming until you add
                      at least one user or one contact type, then click Save policy.
                    </Alert>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={recordStreams}
                    onChange={(e) => setRecordStreams(e.target.checked)}
                  />
                }
                label="Record streams on server (JPEG snapshots, about once per second per user)"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 2 }}>
                Do not record by default — leave unchecked unless your organization requires retained footage.
                Files are stored under the server upload directory in camera-recordings.
              </Typography>

              <Button
                variant="contained"
                startIcon={savingPolicy ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={savePolicy}
                disabled={savingPolicy}
              >
                Save policy
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Active streamers (logged in / publishing)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Everyone currently sending video appears here; thumbnails below update in real time when frames arrive.
      </Typography>
      {loadingList && !streamers.length ? (
        <CircularProgress sx={{ mb: 2 }} />
      ) : (
        <Table size="small" sx={{ mb: 3, maxWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact type</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {streamers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">No active publishers right now.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              streamers.map((st) => (
                <TableRow key={st.userId}>
                  <TableCell>
                    {st.firstName} {st.lastName}
                  </TableCell>
                  <TableCell>{st.email}</TableCell>
                  <TableCell>{st.contactType || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={st.stale ? 'Stale / paused' : 'Receiving video'}
                      color={st.stale ? 'warning' : 'success'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>
        Live video tiles
      </Typography>
      {loadingList && !streamers.length ? (
        <CircularProgress />
      ) : streamers.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          No streams to display.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {streamers.map((st) => (
            <Grid item xs={12} sm={6} md={4} key={st.userId}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" noWrap>
                    {st.firstName} {st.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {st.email}
                  </Typography>
                  {st.contactType && (
                    <Chip size="small" label={st.contactType} sx={{ mt: 0.5 }} variant="outlined" />
                  )}
                  <Box
                    sx={{
                      mt: 1,
                      bgcolor: 'grey.900',
                      borderRadius: 1,
                      aspectRatio: '4/3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {imgUrls[st.userId] ? (
                      <img
                        src={imgUrls[st.userId]}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Typography variant="caption" color="grey.500">
                        Waiting for frame…
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    size="small"
                    label={st.stale ? 'Stale / paused' : 'Receiving'}
                    color={st.stale ? 'warning' : 'success'}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Button startIcon={<RefreshIcon />} onClick={fetchStreamers} sx={{ mb: 3 }}>
        Refresh list
      </Button>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Daily presence (hours)
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          type="date"
          label="Date (UTC day boundary)"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={summaryDate}
          onChange={(e) => setSummaryDate(e.target.value)}
        />
        <Button variant="outlined" onClick={fetchSummary} disabled={loadingSummary}>
          {loadingSummary ? <CircularProgress size={20} /> : 'Load report'}
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell align="right">Live video (h)</TableCell>
            <TableCell align="right">Paused (h)</TableCell>
            <TableCell align="right">Events</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summaries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography color="text.secondary">No events for this date.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            summaries.map((row) => (
              <TableRow key={row.user_id}>
                <TableCell>
                  {row.user
                    ? `${row.user.first_name} ${row.user.last_name} (${row.user.email})`
                    : row.user_id}
                </TableCell>
                <TableCell align="right">{row.streaming_hours}</TableCell>
                <TableCell align="right">{row.paused_hours}</TableCell>
                <TableCell align="right">{row.event_count}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default AdminCameraDashboard;
